/**
 * Vehicle Approval Page
 *
 * Lists all driver vehicles with PENDING_REVIEW status and allows
 * admins to approve or reject them. Prevents drivers from sneaking
 * unapproved vehicles into active use.
 */

import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, Avatar,
  Divider, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Pagination,
} from '@mui/material';
import { CheckCircle, Cancel, DirectionsCar, Visibility } from '@mui/icons-material';
import { LIST_PENDING_VEHICLES } from '../graphql/queries';
import { UPDATE_VEHICLE_STATUS } from '../graphql/mutations';

const client = generateClient();

interface PendingVehicle {
  vehicleId: string;
  driverId: string;
  driverName: string;
  make: string;
  model: string;
  year: number;
  color?: string;
  licensePlate: string;
  vehicleType?: string;
  status: string;
  isDefault: boolean;
  registrationUrl?: string;
  insuranceUrl?: string;
  createdAt: string;
}

const TIER_LABELS: Record<string, string> = {
  economy: 'Economy',
  comfort: 'Comfort',
  business: 'Business',
};

export default function VehicleApproval() {
  const [vehicles, setVehicles] = useState<PendingVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  const [processing, setProcessing] = useState<string | null>(null); // vehicleId being processed

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<PendingVehicle | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Document viewer
  const [docViewerUrl, setDocViewerUrl] = useState<string | null>(null);

  useEffect(() => { fetchPendingVehicles(); }, []);

  const fetchPendingVehicles = async () => {
    try {
      setLoading(true);
      setError(null);
      const result: any = await client.graphql({
        query: LIST_PENDING_VEHICLES,
        variables: { limit: 100 },
      });
      setVehicles(result.data.listPendingVehicles.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (vehicle: PendingVehicle) => {
    setProcessing(vehicle.vehicleId);
    try {
      const result: any = await client.graphql({
        query: UPDATE_VEHICLE_STATUS,
        variables: {
          input: {
            driverId: vehicle.driverId,
            vehicleId: vehicle.vehicleId,
            status: 'APPROVED',
            reviewerName: 'Admin',
          },
        },
      });
      if (result.data.updateVehicleStatus.success) {
        setVehicles(prev => prev.filter(v => v.vehicleId !== vehicle.vehicleId));
      } else {
        setError(result.data.updateVehicleStatus.message || 'Approval failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to approve vehicle');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!selectedVehicle || !rejectionReason.trim()) return;
    setProcessing(selectedVehicle.vehicleId);
    try {
      const result: any = await client.graphql({
        query: UPDATE_VEHICLE_STATUS,
        variables: {
          input: {
            driverId: selectedVehicle.driverId,
            vehicleId: selectedVehicle.vehicleId,
            status: 'REJECTED',
            rejectionReason: rejectionReason.trim(),
            reviewerName: 'Admin',
          },
        },
      });
      if (result.data.updateVehicleStatus.success) {
        setVehicles(prev => prev.filter(v => v.vehicleId !== selectedVehicle.vehicleId));
        setRejectDialogOpen(false);
        setRejectionReason('');
        setSelectedVehicle(null);
      } else {
        setError(result.data.updateVehicleStatus.message || 'Rejection failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reject vehicle');
    } finally {
      setProcessing(null);
    }
  };

  const totalPages = Math.ceil(vehicles.length / itemsPerPage);
  const paginated = vehicles.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Vehicle Approval</Typography>
        <Button variant="outlined" onClick={fetchPendingVehicles}>Refresh</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        These vehicles were added by drivers after onboarding. Review registration and insurance
        documents before approving. Drivers cannot set an unapproved vehicle as their default.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {vehicles.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6">No Pending Vehicles</Typography>
            <Typography color="textSecondary">All vehicle submissions have been reviewed.</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginated.map(vehicle => {
              const busy = processing === vehicle.vehicleId;
              return (
                <Grid item xs={12} md={6} key={vehicle.vehicleId}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          <DirectionsCar color="primary" />
                        </Avatar>
                        <Box flex={1}>
                          <Typography variant="h6">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {vehicle.color} · {vehicle.licensePlate}
                          </Typography>
                        </Box>
                        {vehicle.isDefault && (
                          <Chip label="Default" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                        {vehicle.vehicleType && (
                          <Chip
                            label={TIER_LABELS[vehicle.vehicleType] ?? vehicle.vehicleType}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <Chip label={`Driver: ${vehicle.driverName || vehicle.driverId.substring(0, 8) + '...'}`} size="small" />
                        <Chip
                          label={`Submitted: ${new Date(vehicle.createdAt).toLocaleDateString()}`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      {/* Document links */}
                      <Box display="flex" gap={1} mb={2}>
                        {vehicle.registrationUrl ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => setDocViewerUrl(vehicle.registrationUrl!)}
                          >
                            Registration
                          </Button>
                        ) : (
                          <Chip label="No registration" size="small" color="warning" variant="outlined" />
                        )}
                        {vehicle.insuranceUrl ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => setDocViewerUrl(vehicle.insuranceUrl!)}
                          >
                            Insurance
                          </Button>
                        ) : (
                          <Chip label="No insurance" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>

                      <Box display="flex" gap={2}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                          onClick={() => handleApprove(vehicle)}
                          disabled={busy}
                          fullWidth
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => { setSelectedVehicle(vehicle); setRejectDialogOpen(true); }}
                          disabled={busy}
                          fullWidth
                        >
                          Reject
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Reject dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => !processing && setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Vehicle</DialogTitle>
        <DialogContent>
          {selectedVehicle && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model} — {selectedVehicle.licensePlate}
            </Typography>
          )}
          <TextField
            fullWidth multiline rows={3}
            label="Rejection reason"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="e.g. Insurance document expired, registration unclear..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={!!processing}>Cancel</Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={!rejectionReason.trim() || !!processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document viewer */}
      <Dialog open={!!docViewerUrl} onClose={() => setDocViewerUrl(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6">Document</Typography>
            <Button size="small" variant="outlined" onClick={() => docViewerUrl && window.open(docViewerUrl, '_blank')}>
              Open in New Tab
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {docViewerUrl && (
            <Box component="iframe" src={docViewerUrl} sx={{ width: '100%', height: '100%', border: 'none' }} title="Document" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocViewerUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
